import { cn } from "@/lib/utils";
import { CheckIcon, IndeterminateIcon } from "@ngg/icons";
import type { ForwardedRef, InputHTMLAttributes, ReactNode } from "react";
import { forwardRef } from "react";

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  checked?: boolean;
  disabled?: boolean;
  name?: string;
  children: ReactNode;
  indeterminate?: boolean;
  checkedIcon?: ReactNode;
  uncheckedIcon?: ReactNode;
  indeterminateIcon?: ReactNode;
  rounded?: boolean;
  classNames?: {
    wrapper?: string;
    checkboxContainer?: string;
    checkbox?: string;
    label?: string;
    input?: string;
  };
}

export const Checkbox = forwardRef(
  (
    {
      rounded = false,
      disabled = false,
      checked,
      checkedIcon = <CheckIcon />,
      uncheckedIcon = null,
      indeterminateIcon = <IndeterminateIcon />,
      indeterminate,
      children,
      onChange,
      classNames,
      name,
      ...props
    }: CheckboxProps,
    ref: ForwardedRef<HTMLLabelElement>,
  ) => (
    <label
      ref={ref}
      aria-disabled={disabled}
      className={cn(
        "relative isolate flex items-center p-1",
        classNames?.wrapper,
      )}
    >
      <input
        name={name}
        value=""
        disabled={disabled}
        data-checked={checked}
        data-indeterminate={indeterminate}
        checked={checked}
        onChange={onChange}
        type="checkbox"
        className={cn(
          "peer absolute left-0 top-0 m-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-default",
          classNames?.input,
        )}
        {...props}
      />
      <span
        className={cn(
          "relative block h-em w-em flex-em outline-none before:transition-[border]",
          "before:absolute before:left-0 before:top-0 before:h-full before:w-full before:border before:border-current",
          "peer-checked:before:border-[0.5em] peer-data-indeterminate:before:border-[0.5em]",
          "ring-offset-1 peer-focus-visible:ring peer-disabled:opacity-30",
          rounded ? "rounded-sm before:rounded-sm" : "rounded-none",
          classNames?.checkbox,
        )}
      >
        <div
          className={cn(
            "pointer-events-none relative text-white",
            classNames?.checkboxContainer,
          )}
        >
          {checked ? checkedIcon : null}
          {indeterminate ? indeterminateIcon : null}
          {!checked ? uncheckedIcon : null}
        </div>
      </span>
      <span
        className={cn(
          "ml-2 text-base peer-disabled:opacity-30",
          classNames?.label,
        )}
      >
        {children}
      </span>
    </label>
  ),
);
